import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { filesApi } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  FileSpreadsheet, 
  Clock, 
  Sparkles,
  Table as TableIcon
} from 'lucide-react';

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('en-ZA', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const FileDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [file, setFile] = useState(null);
  const [fileData, setFileData] = useState(null);
  const [selectedSheet, setSelectedSheet] = useState('');

  useEffect(() => {
    loadFile();
  }, [id]);

  const loadFile = async () => {
    try {
      const [fileRes, dataRes] = await Promise.all([
        filesApi.getOne(id),
        filesApi.getData(id)
      ]);
      setFile(fileRes.data);
      setFileData(dataRes.data);
      if (fileRes.data.sheets?.length > 0) {
        setSelectedSheet(fileRes.data.sheets[0]);
      }
    } catch (error) {
      toast.error('Failed to load file');
      navigate('/files');
    } finally {
      setLoading(false);
    }
  };

  const currentSheetData = fileData?.data?.[selectedSheet];

  if (loading) {
    return (
      <div className="p-6 space-y-6" data-testid="file-detail-loading">
        <Skeleton className="h-10 w-64 rounded-sm" />
        <Skeleton className="h-96 rounded-sm" />
      </div>
    );
  }

  if (!file) {
    return null;
  }

  return (
    <div className="p-6 space-y-6" data-testid="file-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button 
          variant="ghost" 
          size="icon"
          onClick={() => navigate('/files')}
          className="rounded-sm"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
        </Button>
        <div className="flex-1">
          <h1 className="font-display text-2xl font-light text-slate-900 truncate">
            {file.filename}
          </h1>
          <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {formatDate(file.uploaded_at)}
            </span>
            <span>{file.sheets?.length || 0} sheets</span>
            <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-sm text-xs">
              {file.status}
            </span>
          </div>
        </div>
        <Button 
          onClick={() => navigate('/insights')}
          className="bg-violet-600 hover:bg-violet-700 text-white rounded-sm"
          data-testid="generate-insights-btn"
        >
          <Sparkles className="w-4 h-4 mr-2" strokeWidth={1.5} />
          Generate Insights
        </Button>
      </div>

      {/* Sheets Tabs */}
      {file.sheets?.length > 0 && (
        <Card className="border border-slate-200 shadow-none rounded-sm">
          <Tabs value={selectedSheet} onValueChange={setSelectedSheet}>
            <CardHeader className="border-b border-slate-100 pb-0">
              <ScrollArea className="w-full">
                <TabsList className="bg-transparent h-auto p-0 gap-0">
                  {file.sheets.slice(0, 10).map((sheet) => (
                    <TabsTrigger 
                      key={sheet} 
                      value={sheet}
                      className="rounded-none border-b-2 border-transparent data-[state=active]:border-zinc-900 data-[state=active]:bg-transparent px-4 py-3 text-sm"
                      data-testid={`sheet-tab-${sheet}`}
                    >
                      {sheet.length > 20 ? `${sheet.slice(0, 20)}...` : sheet}
                    </TabsTrigger>
                  ))}
                  {file.sheets.length > 10 && (
                    <span className="px-4 py-3 text-sm text-slate-400">
                      +{file.sheets.length - 10} more
                    </span>
                  )}
                </TabsList>
              </ScrollArea>
            </CardHeader>
            
            <CardContent className="p-0">
              {file.sheets.slice(0, 10).map((sheet) => (
                <TabsContent key={sheet} value={sheet} className="m-0">
                  {fileData?.data?.[sheet] ? (
                    <div className="overflow-x-auto">
                      <table className="w-full financial-table" data-testid={`table-${sheet}`}>
                        <thead>
                          <tr className="border-b border-slate-200">
                            {fileData.data[sheet].columns?.slice(0, 10).map((col, i) => (
                              <th key={i} className="text-left p-3 font-medium text-xs bg-slate-50 whitespace-nowrap">
                                {String(col).slice(0, 25)}
                                {String(col).length > 25 && '...'}
                              </th>
                            ))}
                            {(fileData.data[sheet].columns?.length || 0) > 10 && (
                              <th className="text-left p-3 font-medium text-xs bg-slate-50 text-slate-400">
                                +{fileData.data[sheet].columns.length - 10} cols
                              </th>
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          {fileData.data[sheet].rows?.slice(0, 50).map((row, rowIdx) => (
                            <tr key={rowIdx} className="border-b border-slate-50 hover:bg-slate-50">
                              {fileData.data[sheet].columns?.slice(0, 10).map((col, colIdx) => (
                                <td key={colIdx} className="p-3 text-sm font-mono whitespace-nowrap">
                                  {row[col] !== null && row[col] !== undefined 
                                    ? String(row[col]).slice(0, 40)
                                    : '-'}
                                  {String(row[col] || '').length > 40 && '...'}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      
                      {/* Data info */}
                      <div className="p-4 bg-slate-50 border-t border-slate-100 text-sm text-slate-500">
                        Showing {Math.min(50, fileData.data[sheet].rows?.length || 0)} of {fileData.data[sheet].shape?.[0] || 0} rows
                        {' · '}
                        {fileData.data[sheet].shape?.[1] || 0} columns
                      </div>
                    </div>
                  ) : (
                    <div className="p-12 text-center text-slate-500">
                      <TableIcon className="w-12 h-12 mx-auto mb-4 text-slate-300" strokeWidth={1.5} />
                      <p>No data available for this sheet</p>
                    </div>
                  )}
                </TabsContent>
              ))}
            </CardContent>
          </Tabs>
        </Card>
      )}

      {/* Sheet Identification */}
      {(fileData?.budget_sheets?.length > 0 || fileData?.cashflow_sheets?.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {fileData?.budget_sheets?.length > 0 && (
            <Card className="border border-emerald-100 bg-emerald-50/30 shadow-none rounded-sm">
              <CardContent className="p-4">
                <h3 className="font-medium text-emerald-900 mb-2">Budget Sheets Detected</h3>
                <div className="flex flex-wrap gap-2">
                  {fileData.budget_sheets.map((sheet, i) => (
                    <span 
                      key={i}
                      className="px-2 py-1 bg-emerald-100 text-emerald-800 text-xs rounded-sm cursor-pointer hover:bg-emerald-200"
                      onClick={() => setSelectedSheet(sheet)}
                    >
                      {sheet}
                    </span>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
          
          {fileData?.cashflow_sheets?.length > 0 && (
            <Card className="border border-blue-100 bg-blue-50/30 shadow-none rounded-sm">
              <CardContent className="p-4">
                <h3 className="font-medium text-blue-900 mb-2">Cashflow Sheets Detected</h3>
                <div className="flex flex-wrap gap-2">
                  {fileData.cashflow_sheets.map((sheet, i) => (
                    <span 
                      key={i}
                      className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-sm cursor-pointer hover:bg-blue-200"
                      onClick={() => setSelectedSheet(sheet)}
                    >
                      {sheet}
                    </span>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};

export default FileDetailPage;
